/**
 * Derives loanSizeTier (conforming | high_balance | jumbo) from loan amount + county limit.
 */
import limitsData from '../data/countyConformingLimits.json';

export const FHFA_BASELINE_LIMIT = limitsData.baseline || 806500;

const HIGH_COST = limitsData.highCost || {};

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export function computeLoanAmount(answers = {}) {
  const value = num(answers.estimatedValue);
  const cash = num(answers.downPayment);
  if (answers.purchaseOrRefinance === 'refinance') return cash;
  return Math.max(0, value - cash);
}

export function getCountyConformingLimit(stateCode, countyName) {
  const state = String(stateCode || '').toUpperCase();
  const county = String(countyName || '').trim();
  if (!state || !county) return FHFA_BASELINE_LIMIT;
  const key = `${state}|${county}`;
  return HIGH_COST[key] || FHFA_BASELINE_LIMIT;
}

/**
 * @returns {'conforming'|'high_balance'|'jumbo'|''}
 */
export function deriveLoanSizeTier(answers = {}) {
  const loanAmount = computeLoanAmount(answers);
  if (loanAmount <= 0) return '';

  const state = answers.propertyState;
  const county = answers.propertyCounty;
  if (!state || !county) {
    if (loanAmount <= FHFA_BASELINE_LIMIT) return 'conforming';
    return 'jumbo';
  }

  const countyLimit = getCountyConformingLimit(state, county);

  if (loanAmount <= FHFA_BASELINE_LIMIT) return 'conforming';
  if (loanAmount <= countyLimit) return 'high_balance';
  return 'jumbo';
}

export function isNearLimitBoundary(answers = {}, marginPct = 0.05) {
  const loanAmount = computeLoanAmount(answers);
  if (loanAmount <= 0) return false;
  const limit = getCountyConformingLimit(answers.propertyState, answers.propertyCounty)
    || FHFA_BASELINE_LIMIT;
  const margin = limit * marginPct;
  return Math.abs(loanAmount - FHFA_BASELINE_LIMIT) <= margin
    || Math.abs(loanAmount - limit) <= margin;
}

export function buildDerivedAssessmentState(answers = {}) {
  const loanAmount = computeLoanAmount(answers);
  const tier = deriveLoanSizeTier(answers);
  const countyLimit = answers.propertyState && answers.propertyCounty
    ? getCountyConformingLimit(answers.propertyState, answers.propertyCounty)
    : FHFA_BASELINE_LIMIT;

  return {
    loanAmount,
    loanSizeTier: tier || answers.loanSizeTier || null,
    countyConformingLimit: countyLimit,
    baselineLimit: FHFA_BASELINE_LIMIT,
    hasLocation: Boolean(answers.propertyState && answers.propertyCounty),
    hasZip: Boolean(String(answers.propertyZip || '').length === 5),
    nearLimitBoundary: isNearLimitBoundary(answers),
  };
}
